import { useQueries } from '@tanstack/react-query';
import BiService from '@app/model/bi/service/BiService';
import { BI_KEYS } from '@app/model/bi/service/BiHooks';
import type { EvolutionGrain, SegmentationReport, SegmentationView, SegmentEvolution } from '@app/model/bi/Bi';
import { limiter, type CompareItem } from './Compare.utilities';

// The data of a Property comparison: the CURRENT queries of the report, one call for each item (T1, T7). The keys are
// BI_KEYS of the report pages, so a report seen before reads the cache, and a save of the Segment Groups refreshes the
// comparison with the report. At most 3 calls run at the same time; the others wait (S2). Each item loads and fails alone
// (T11). No placeholderData: inside useQueries a new key gets a new observer with no previous data, so a column that
// reloads shows "…" (grill Q4).

// The stale time of the report hooks (BiHooks BI_STALE_TIME; S1: BiHooks.ts does not change).
const STALE_TIME = 5 * 60 * 1000;
// One runner for the page: the table calls and the chart calls share it (grill Q5: one comparison page mounts at a time).
const limited = limiter(3);

export type ItemData<T> = { data: T | undefined, state: 'loading' | 'error' | 'ready', refetch: () => void };

export const useCompareReports = (items: CompareItem[], stayFrom: string, stayTo: string, view: SegmentationView, includeOptions: boolean,
	enabled: boolean): ItemData<SegmentationReport>[] =>
	useQueries({
		queries: items.map(item => ({
			queryKey: BI_KEYS.segmentation(item.companyHotelId, item.propertyGroupId, stayFrom, stayTo, view, includeOptions),
			queryFn: () => limited(() => BiService.findSegmentationReport(item.companyHotelId, item.propertyGroupId, stayFrom, stayTo, view, includeOptions)),
			staleTime: STALE_TIME,
			enabled,
		})),
	}).map(q => ({ data: q.data, state: q.isError ? 'error' : (q.data === undefined ? 'loading' : 'ready'), refetch: () => void q.refetch() }));

export const useCompareEvolutions = (items: CompareItem[], stayFrom: string, stayTo: string, grain: EvolutionGrain, view: SegmentationView,
	includeOptions: boolean, enabled: boolean): ItemData<SegmentEvolution>[] =>
	useQueries({
		queries: items.map(item => ({
			queryKey: BI_KEYS.evolution(item.companyHotelId, item.propertyGroupId, stayFrom, stayTo, grain, view, includeOptions),
			queryFn: () => limited(() => BiService.findSegmentEvolution(item.companyHotelId, item.propertyGroupId, stayFrom, stayTo, grain, view, includeOptions)),
			staleTime: STALE_TIME,
			enabled,
		})),
	}).map(q => ({ data: q.data, state: q.isError ? 'error' : (q.data === undefined ? 'loading' : 'ready'), refetch: () => void q.refetch() }));
